import { useState } from 'react'
import { Integration } from '@integration-app/sdk'

import { AppLogo } from 'components/Icons/AppLogo'
import { cva, cx, RecipeVariantProps } from 'styled-system/css'
import { Icon, IconProps } from 'ui-kit/icon'

const integrationIconRecipe = cva({
  base: {
    maxWidth: 'inherit',
    color: 'fg.default',
  },
  variants: {
    error: {
      true: {
        color: 'error.emphasized',
      },
    },
    variant: {
      rounded: {
        borderRadius: 'l1',
      },
      square: {},
    },
    size: {
      _undef: {},
      sm: {
        width: '4',
        height: '4',
      },
      md: {
        width: '6',
        height: '6',
      },
      lg: {
        width: '8',
        height: '8',
      },
      xl: {
        width: '10',
        height: '10',
      },
    },
  },
  defaultVariants: {
    variant: 'rounded',
    size: 'md',
  },
  compoundVariants: [
    {
      size: 'lg',
      variant: 'rounded',
      css: {
        borderRadius: 'l2',
      },
    },
    {
      size: 'xl',
      variant: 'rounded',
      css: {
        borderRadius: 'l2',
      },
    },
    {
      size: '_undef',
      variant: 'rounded',
      css: {
        borderRadius: 'l2',
      },
    },
  ],
})
export type IntegrationIconProps = {
  integration?: Partial<Integration> & Pick<Integration, 'name' | 'logoUri'>
} & RecipeVariantProps<typeof integrationIconRecipe> &
  Omit<IconProps, 'size'>

export function IntegrationIcon({
  integration,
  size = 'md',
  variant = 'rounded',
  className,
  ...props
}: IntegrationIconProps) {
  const [error, setError] = useState(false)
  const classnames = integrationIconRecipe({
    variant,
    size: props.width || props.height ? '_undef' : size,
    error,
  })

  if (!integration) return null

  if (error) {
    return (
      <DefaultIntegrationIcon error size={size} variant={variant} {...props} />
    )
  }

  if (integration?.logoUri) {
    return (
      <Icon className={cx(classnames, className)} {...props}>
        <img
          src={integration?.logoUri}
          alt={integration?.name}
          onError={() => setError(true)}
        />
      </Icon>
    )
  }

  return <DefaultIntegrationIcon size={size} variant={variant} {...props} />
}

export function DefaultIntegrationIcon({
  size = 'md',
  variant = 'rounded',
  error = false,
  className,
  ...props
}: Omit<IntegrationIconProps, 'integration'>) {
  const classnames = integrationIconRecipe({
    variant,
    size: props.width || props.height ? '_undef' : size,
    error,
  })

  return (
    <Icon className={cx(classnames, className)} {...props}>
      <AppLogo />
    </Icon>
  )
}
